import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import AuthRequiredLink from './AuthRequiredLink';
import './Header.css';

interface HeaderProps {
  invert?: boolean;
}

export default function Header({ invert = false }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const isHome = location.pathname === '/';

  useEffect(() => {
    // 初期化時にlocalStorageからテーマ設定を読み込む
    const darkMode = localStorage.getItem('luxiDarkMode') === 'true';
    setIsDarkMode(darkMode);
    
    const appElement = document.getElementById('app');
    if (appElement) {
      if (darkMode) {
        appElement.classList.remove('theme--light');
        appElement.classList.add('theme--dark');
      } else {
        appElement.classList.remove('theme--dark');
        appElement.classList.add('theme--light');
      }
    }
  }, []);

  useEffect(() => {
    // スクロール時にヘッダーに背景を表示する処理
    const headerElement = document.getElementById('header');
    if (!headerElement) return;

    let sticky = 0;
    if (headerElement) {
      sticky = headerElement.offsetTop + 100;
    }

    const handleScroll = () => {
      if (window.pageYOffset > sticky) {
        headerElement.classList.add('fixed');
      } else {
        headerElement.classList.remove('fixed');
      }
    };

    // 初期状態をチェック
    handleScroll();

    // スクロールイベントリスナーを追加
    window.addEventListener('scroll', handleScroll);
    // ハッシュ付き遷移（/#feature など）だと、初回handleScroll実行時点ではまだスクロール位置が反映されていないことがあるため、
    // 次フレーム/少し後にも再評価して確実に fixed 状態を同期する
    const t0 = window.setTimeout(handleScroll, 0);
    const t1 = window.setTimeout(handleScroll, 120);
    const t2 = window.setTimeout(handleScroll, 400);
    // ハッシュが変わるケースにも追従（通常のアンカー遷移時など）
    window.addEventListener('hashchange', handleScroll);

    // クリーンアップ
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('hashchange', handleScroll);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  const handleThemeToggle = (checked: boolean) => {
    setIsDarkMode(checked);
    localStorage.setItem('luxiDarkMode', checked ? 'true' : 'false');
    
    const appElement = document.getElementById('app');
    if (appElement) {
      if (checked) {
        appElement.classList.remove('theme--light');
        appElement.classList.add('theme--dark');
      } else {
        appElement.classList.remove('theme--dark');
        appElement.classList.add('theme--light');
      }
    }
  };
  
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // LandingPageにいる場合はトップにスクロール
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // 公開パスからホームへの遷移の場合はページをリロード
      const isPublicPath = ['/privacy-policy', '/terms-of-service', '/commercial-transactions', '/account-deletion', '/login'].includes(location.pathname);
      if (isPublicPath) {
        window.location.href = '/';
      } else {
        // それ以外のページの場合はトップページに遷移
        navigate('/');
      }
    }
  };

  return (
    <>
      <div className={`sidenav mobile-nav ${invert ? 'invert' : ''}`} id="slide-menu">
        <div className="menu">
          <ul className="collection">
            <li className="collection-item" style={{ animationDuration: '0.25s' }}>
              <a className="sidenav-close waves-effect menu-list" href={isHome ? '#feature-title' : '/#feature-title'}>特徴</a>
            </li>
            <li className="collection-item" style={{ animationDuration: '0.5s' }}>
              <a className="sidenav-close waves-effect menu-list" href={isHome ? '#showcase-title' : '/#showcase-title'}>画面イメージ</a>
            </li>
            <li className="collection-item" style={{ animationDuration: '0.75s' }}>
              <a className="sidenav-close waves-effect menu-list" href={isHome ? '#testimonials' : '/#testimonials'}>testimonials</a>
            </li>
            <li className="collection-item" style={{ animationDuration: '1s' }}>
              <a className="sidenav-close waves-effect menu-list" href="/faq">FAQ</a>
            </li>
            <li className="collection-item" style={{ animationDuration: '1s' }}>
              <AuthRequiredLink className="waves-effect menu-list" to="/contact">contact</AuthRequiredLink>
            </li>
          </ul>
        </div>
      </div>
      <header className={`app-bar header ${invert ? 'invert' : ''}`} id="header">
        <div className="container fixed-width-lg-up">
          <div className="header-content">
            <nav className={`nav-logo nav-menu ${invert ? 'invert' : ''}`}>
              <button
                className="mobile-menu btn-icon waves-effect hamburger hamburger--spin show-md-down"
                id="mobile_menu"
                type="button"
              >
                <span className="hamburger-box">
                  <span className="bar hamburger-inner"></span>
                </span>
              </button>
              <div className={`logo ${!invert && isHome ? 'scrollnav' : ''}`}>
                <a href="/" onClick={handleLogoClick}>
                  <img src="/assets/images/mobile-logo.png" alt="logo" />
                  <span>LessonMate</span>
                </a>
              </div>
              <div>
                <div className={`scrollactive-nav show-lg-up ${!invert && isHome ? 'scrollnav' : ''}`}>
                  <ul>
                    <li className="d-none">
                      <a href={isHome ? '#banner' : '/#banner'}></a>
                    </li>
                    <li>
                      <a className="btn btn-flat anchor-link waves-effect" href={isHome ? '#feature-title' : '/#feature-title'}>
                        特徴
                      </a>
                    </li>
                    <li>
                      <a className="btn btn-flat anchor-link waves-effect" href={isHome ? '#showcase-title' : '/#showcase-title'}>
                        画面イメージ
                      </a>
                    </li>
                    {/* <li>
                      <a
                        className="btn btn-flat anchor-link waves-effect"
                        href={isHome ? '#testimonials' : '/#testimonials'}
                      >
                        testimonials
                      </a>
                    </li> */}
                    <li>
                      <a className="btn btn-flat anchor-link waves-effect" href="/faq">
                        FAQ
                      </a>
                    </li>
                    <li>
                      <AuthRequiredLink className="btn btn-flat anchor-link waves-effect" to="/contact">
                        お問い合わせ
                      </AuthRequiredLink>
                    </li>
                  </ul>
                </div>
              </div>
            </nav>
            <nav className={`nav-menu ${invert ? 'invert' : ''}`}>
              {/* {!invert && (
                <div className="hidden-xs-down">
                  <div className="btn btn-icon social-btn waves-effect">
                    <i className="ion-social-twitter"></i>
                  </div>
                  <div className="btn btn-icon social-btn waves-effect">
                    <i className="ion-social-facebook"></i>
                  </div>
                  <div className="btn btn-icon social-btn waves-effect">
                    <i className="ion-social-instagram"></i>
                  </div>
                  <div className="btn btn-icon social-btn waves-effect">
                    <i className="ion-social-linkedin"></i>
                  </div>
                </div>
              )} */}
              <div className="theme-switcher my-3 my-sm-3">
                <div className="switch">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="ion-ios-sunny" style={{ fontSize: '18px', lineHeight: '1' }}></i>
                    <input
                      id="theme_switcher"
                      type="checkbox"
                      checked={isDarkMode}
                      onChange={e => handleThemeToggle(e.target.checked)}
                    />
                    <span className="lever"></span>
                    <i className="ion-ios-moon" style={{ fontSize: '18px', lineHeight: '1' }}></i>
                  </label>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
}

